import { Rnd } from "react-rnd"

// Paint answer text in the same font fitAnswerText measures with (Helvetica, matching the PDF), so
// the interactive fit never diverges from what is drawn — and from the downloaded PDF. Single source
// of truth lives in fitAnswerText. Only applied when fitText is on (USE_EXTEND).
import { PDF_ANSWER_FONT_FAMILY } from "@/utils/questionnaire/fitAnswerText"

type PdfHighlightBoxProps = {
  layout: {
    width: number
    height: number
    scale: number
    x: number
    y: number
    fontSize?: number
  }
  onResize: (
    width: number | string,
    height: number | string,
    x: number,
    y: number,
  ) => void
  onDrag: (x: number, y: number) => void
  children?: React.ReactNode
  className?: string
  id: string
  onBoxClick?: () => void
  bounds?: string
  isSelected?: boolean
  /** When true, wraps children in an overflow-hidden div and sets lineHeight:1.2 so text fits
   *  the cell the same way the server-rendered PDF does. Gated on USE_EXTEND by the caller. */
  fitText?: boolean
  /** "signature" renders `imageUrl` as an image instead of `children`, and skips
   *  the fitText wrapper — there is no text to fit. */
  kind?: "answer" | "signature"
  imageUrl?: string
  /** Passed straight through to react-rnd. Signature boxes lock it so a resize
   *  can't distort the ink. */
  lockAspectRatio?: boolean
}

export const PdfHighlightBox = ({
  layout: { width, height, scale, x, y, fontSize },
  onResize,
  onDrag,
  className,
  children,
  id,
  onBoxClick,
  bounds,
  isSelected,
  fitText = false,
  kind,
  imageUrl,
  lockAspectRatio,
}: PdfHighlightBoxProps) => {
  const handleBaseClass =
    "pointer-events-auto absolute h-[5px] w-[5px] rounded-full bg-black shadow-lg ring-2 ring-primary"

  // `react-rnd` uses `re-resizable`. Each handle is its own absolutely positioned element.
  // For corner handles, the library defaults to a 20px x 20px box (with -10px offsets).
  // This is the element you're seeing in DevTools.
  //
  // `resizeHandleWrapperStyle` only styles the *common wrapper* around all handles, not
  // each handle element. To change the per-handle box, we must use `resizeHandleStyles`.
  const resizeHandleStyles = isSelected
    ? {
        topLeft: {
          width: "0px",
          height: "0px",
          left: "0px",
          top: "0px",
          padding: 0,
          border: 0,
          background: "transparent",
        },
        topRight: {
          width: "0px",
          height: "0px",
          right: "0px",
          top: "0px",
          padding: 0,
          border: 0,
          background: "transparent",
        },
        bottomLeft: {
          width: "0px",
          height: "0px",
          left: "0px",
          bottom: "0px",
          padding: 0,
          border: 0,
          background: "transparent",
        },
        bottomRight: {
          width: "0px",
          height: "0px",
          right: "0px",
          bottom: "0px",
          padding: 0,
          border: 0,
          background: "transparent",
        },
      }
    : undefined

  const handleComponent = isSelected
    ? {
        topLeft: (
          <div
            className={`${handleBaseClass} left-0 top-0 -translate-x-1/2 -translate-y-1/2`}
          />
        ),
        topRight: (
          <div
            className={`${handleBaseClass} right-0 top-0 -translate-y-1/2 translate-x-1/2`}
          />
        ),
        bottomLeft: (
          <div
            className={`${handleBaseClass} bottom-0 left-0 -translate-x-1/2 translate-y-1/2`}
          />
        ),
        bottomRight: (
          <div
            className={`${handleBaseClass} bottom-0 right-0 translate-x-1/2 translate-y-1/2`}
          />
        ),
      }
    : undefined

  return (
    <Rnd
      bounds={bounds}
      lockAspectRatio={lockAspectRatio}
      enableResizing={{
        top: false,
        right: false,
        bottom: false,
        left: false,
        topLeft: true,
        topRight: true,
        bottomLeft: true,
        bottomRight: true,
      }}
      resizeHandleComponent={handleComponent}
      resizeHandleStyles={resizeHandleStyles}
      onDragStart={() => {
        onBoxClick?.()
      }}
      onResizeStart={() => {
        onBoxClick?.()
      }}
      id={`${id}`}
      size={{ width, height }}
      position={{ x, y }}
      onDragStop={(e, d) => {
        onDrag(d.x, d.y)
      }}
      onResizeStop={(e, direction, ref, delta, position) => {
        onResize(ref.style.width, ref.style.height, position.x, position.y)
      }}
      style={{
        fontSize: fontSize ? fontSize * scale : 10 * scale,
        ...(fitText
          ? { lineHeight: 1.2, fontFamily: PDF_ANSWER_FONT_FAMILY }
          : {}),
      }}
      className={`absolute z-50 border-[0.5px] border-chart-3 text-black hover:border-black hover:bg-chart-2/20 ${isSelected ? "border! border-black! bg-chart-2/30" : ""} ${fitText ? "" : (className ?? "")}`}
    >
      {kind === "signature" ? (
        <img
          className="h-full w-full object-contain"
          src={imageUrl}
          alt=""
          draggable={false}
        />
      ) : fitText ? (
        /* Inner wrapper mirrors how the server-rendered PDF lays text into the cell: the font is
           already shrunk to fit (see fitAnswerText), then the text wraps and is clipped to the box.
           overflow-hidden lives here — NOT on the Rnd — so the corner resize handles, which sit
           half outside the box, are not clipped. className carries per-box alignment. */
        <div
          className={`h-full w-full overflow-hidden ${className ?? ""}`}
          style={{ whiteSpace: "pre-wrap", overflowWrap: "break-word" }}
        >
          {children}
        </div>
      ) : (
        children
      )}
    </Rnd>
  )
}
